import { useState, useCallback, useEffect, useMemo } from 'preact/hooks';
import { STRINGS_SV, STRINGS_EN, type BitioBuilderConfig, type ContactInfo, type ConversationMode, type LeaveMessageRequest } from '@bitio-builder/shared';
import { ChatBubble } from './ChatBubble';
import { ChatWindow } from './ChatWindow';
import { useChat } from '../hooks/useChat';
import { initializeApi, submitLeaveMessage, type UserIntro } from '../hooks/useApi';
import { isWithinOfficeHours, DEFAULT_OFFICE_HOURS } from '../utils/officeHours';

const CHAT_CLOSED_KEY = 'bitio-builder-chat-closed';

interface BitioBuilderProps {
  config: BitioBuilderConfig;
}

const containerStyle: Record<string, string | number> = {
  position: 'fixed',
  zIndex: 999999,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontSize: '14px',
  lineHeight: 1.5,
};

const bottomRightStyle: Record<string, string | number> = {
  ...containerStyle,
  bottom: '20px',
  right: '20px',
};

const bottomLeftStyle: Record<string, string | number> = {
  ...containerStyle,
  bottom: '20px',
  left: '20px',
};

export function BitioBuilder({ config }: BitioBuilderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const language = config.language || 'sv';
  const strings = language === 'sv' ? STRINGS_SV : STRINGS_EN;
  const position = config.position || 'bottom-right';
  const title = config.title || 'Bitio Builder';

  useEffect(() => {
    const apiUrl = config.apiUrl || 'https://api.bitiobuilder.se/api';
    initializeApi(config.apiKey, apiUrl);
  }, [config.apiKey, config.apiUrl]);

  // Client-side office hours check
  const withinOfficeHours = useMemo(() => {
    return isWithinOfficeHours(config.officeHours || DEFAULT_OFFICE_HOURS);
  }, [config.officeHours]);

  // Auto-open chat after 5 seconds if user hasn't closed it before
  useEffect(() => {
    let hasUserClosedChat = false;
    try {
      hasUserClosedChat = localStorage.getItem(CHAT_CLOSED_KEY) === 'true';
    } catch {
      // localStorage may not be available
    }

    if (hasUserClosedChat) {
      return;
    }

    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const noReplyTimeout = config.noReplyTimeout ?? 60;
  const chat = useChat(language, noReplyTimeout);

  const handleToggle = useCallback(() => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    // Track when user explicitly closes the chat
    if (!newIsOpen) {
      try {
        localStorage.setItem(CHAT_CLOSED_KEY, 'true');
      } catch {
        // localStorage may not be available
      }
    }
    // Don't auto-start conversation - let user select mode first
  }, [isOpen]);

  const handleSelectMode = useCallback((mode: ConversationMode, userIntro?: UserIntro) => {
    chat.startConversation(mode, userIntro);
  }, [chat]);

  const handleSubmitContact = useCallback(
    async (contactInfo: ContactInfo) => {
      await chat.completeConversation(contactInfo);

      if (config.onLeadSubmitted && chat.conversationId) {
        config.onLeadSubmitted(chat.conversationId);
      }
    },
    [chat, config]
  );

  const handleLeaveMessage = useCallback(() => {
    chat.showLeaveMessageForm();
  }, [chat]);

  const handleHideLeaveMessage = useCallback(() => {
    chat.hideLeaveMessageForm();
  }, [chat]);

  const handleSubmitLeaveMessage = useCallback(
    async (data: LeaveMessageRequest): Promise<boolean> => {
      // Add language and conversation messages to the request
      const dataWithContext: LeaveMessageRequest = {
        ...data,
        conversationMessages: chat.messages,
        metadata: {
          ...data.metadata,
          language,
        },
      };

      const response = await submitLeaveMessage(dataWithContext);
      return response.success;
    },
    [language, chat.messages]
  );

  const positionStyle = position === 'bottom-left' ? bottomLeftStyle : bottomRightStyle;

  // Apply custom CSS variables
  const customVars = {
    '--bb-primary': config.primaryColor || '#4D5D32',
    '--bb-accent': config.accentColor || '#FA9EAF',
    '--bb-background': config.backgroundColor || '#FAFAFA',
  } as Record<string, string>;

  return (
    <div style={{ ...positionStyle, ...customVars }}>
      <ChatWindow
        isOpen={isOpen}
        status={chat.status}
        mode={chat.mode}
        title={title}
        messages={chat.messages}
        showSpecReview={chat.showSpecReview}
        showContactForm={chat.shouldShowContactForm}
        showLeaveMessage={chat.showLeaveMessage}
        showNoReplyFallback={chat.showNoReplyFallback}
        isWithinOfficeHours={withinOfficeHours}
        error={chat.error}
        strings={strings}
        projectSpec={chat.projectSpec}
        conversationSummary={chat.conversationSummary}
        isWaitingForResponse={chat.isWaitingForResponse}
        uploadedAttachments={chat.uploadedAttachments}
        onSendMessage={chat.sendMessage}
        onFileUpload={chat.uploadFile}
        onRemoveAttachment={chat.removeAttachment}
        onSubmitContact={handleSubmitContact}
        onConfirmSpec={chat.confirmSpec}
        onContinueRefining={chat.continueRefining}
        onSubmitSpecEarly={chat.submitSpecEarly}
        onSelectMode={handleSelectMode}
        onLeaveMessage={handleLeaveMessage}
        onHideLeaveMessage={handleHideLeaveMessage}
        onSubmitLeaveMessage={handleSubmitLeaveMessage}
        onReset={chat.reset}
        onClose={handleToggle}
      />
      <ChatBubble isOpen={isOpen} onClick={handleToggle} />
    </div>
  );
}
